import React, { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useContent } from '../contexts/ContentContext'
import CtaButton from './ui/CtaButton'
import { useNavigation } from '../contexts/NavigationContext'
import { useSite } from '../contexts/SiteContext'

export default function SidecarMenu({ isOpen, onClose }) {
  const { menu } = useContent()
  const { currentLang } = useSite()
  const routerNavigate = useNavigate()
  const [isClosing, setIsClosing] = React.useState(false)
  const closeTimeoutRef = useRef(null)
  const { navigateTo, isBurgerClosing } = useNavigation()

  const shouldAnimateOut = isClosing || isBurgerClosing
  const isBackdropVisible = isOpen || shouldAnimateOut

  const handleClose = () => {
    setIsClosing(true)
    closeTimeoutRef.current = setTimeout(() => {
      onClose()
      setIsClosing(false)
    }, 500)
  }

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current)
      }
    }
  }, [])

  const handleNavClick = (child) => {
    handleClose()
    if (child?.href) {
      const href = child.href.replace(/\/(de|en)$/, `/${currentLang}`)
      routerNavigate(href)
      return
    }
    if (child?.anchor) navigateTo(child.anchor)
  }

  if (!isOpen && !isClosing && !isBurgerClosing) return null

  return (
    <>
      {isBackdropVisible && (
        <div
          className={`fixed inset-0 bg-black/20 backdrop-blur z-[60] ${
            shouldAnimateOut
              ? 'opacity-0 transition-opacity duration-[400ms] md:duration-[900ms] ease-out'
              : 'opacity-100 animate-[fadeIn_250ms_ease-in]'
          }`}
          onClick={handleClose}
        />
      )}

      <div
        className="fixed top-0 right-0 h-full w-full 900:w-[380px] bg-color-bg-light shadow-2xl z-[70] flex flex-col"
        style={{
          animation: shouldAnimateOut
            ? 'slideOutToRight 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards'
            : 'slideInFromRight 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards'
        }}
      >
        <div className="flex items-center px-8 py-2">
          <h2 className="typography-h2">
            {menu.overviewLabel}
          </h2>
        </div>

        <nav className="flex-1 overflow-y-auto px-5 py-2">
          <div className="grid grid-cols-2 900:grid-cols-1 gap-x-6 gap-y-0">
            {menu.items.map((item, index) => (
              <div key={item.id} className={index > 0 ? 'mt-4' : ''}>
                {item.children ? (
                  <>
                    <div className="font-primary text-menu-text text-color-primary leading-[1.44] py-0.5">
                      {item.label}
                    </div>
                    <div className="pl-3 pb-1 space-y-0">
                      {item.children.map((child) => (
                        <button
                          key={child.id}
                          onClick={() => handleNavClick(child)}
                          className="block w-full text-left font-primary text-menu-text text-color-text hover:text-color-primary leading-[1.44] py-0.5 px-2 rounded hover:bg-black/5 transition-colors"
                        >
                          {child.label}
                        </button>
                      ))}
                    </div>
                  </>
                ) : (
                  <button
                    onClick={() => handleNavClick(item)}
                    className="w-full text-left font-primary text-menu-text text-color-primary hover:text-color-secondary leading-[1.44] py-0.5 transition-colors"
                  >
                    {item.label}
                  </button>
                )}
              </div>
            ))}
          </div>
        </nav>

        <div className="px-8 py-4">
          <CtaButton
            label={menu.header.cta.label}
            variant="primary"
            onClick={() => {
              onClose?.()
              window.dispatchEvent(new CustomEvent('booking:open'))
            }}
            className="w-full"
          />
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes fadeOut {
          from { opacity: 1; }
          to { opacity: 0; }
        }
        @keyframes slideInFromRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        @keyframes slideOutToRight {
          from { transform: translateX(0); }
          to { transform: translateX(100%); }
        }
      `}</style>
    </>
  )
}
